import { FormikProps } from 'formik'
import { useEffect, useState } from 'react'
import { IS_LIVE } from '../../constants'
import {
  useFetchBanksQuery,
  useResolveBankDetailsQuery,
} from '../../redux-toolkits/misc/bank.slice'
import { QueryProps } from '../../redux-toolkits/shared/shared.type'
import { SettlementAccount } from '../../redux-toolkits/settlements/settlements.type'
import ComboBox from '../inputs/combobox'
import { TextInput } from '../inputs/textInput'

export const AccountDetails = ({
  onChange = () => {},
  formik,
}: {
  onChange: (v: SettlementAccount) => void
  formik: FormikProps<any>
}) => {
  const [query, setQuery] = useState<QueryProps>({
    search: undefined,
  })

  const { values, errors, handleChange, handleBlur, touched, setFieldValue } =
    formik as FormikProps<SettlementAccount>

  const {
    data: accountDetails,
    isFetching: resolvingAccount,
    isError: resolveAccountError,
  } = useResolveBankDetailsQuery(
    {
      accountNumber: values?.accountNumber,
      bankCode: values?.bankCode,
    },
    {
      refetchOnMountOrArgChange: true,
      skip:
        !values?.accountNumber ||
        values.accountNumber.length !== 10 ||
        !values?.bankCode,
    },
  )

  const { data: banks, isFetching: fetchingBanks } = useFetchBanksQuery(
    undefined,
    {
      refetchOnMountOrArgChange: true,
    },
  )

  useEffect(() => {
    onChange(values)
  }, [values])

  useEffect(() => {
    if (accountDetails?.data?.account_name) {
      setFieldValue('accountName', accountDetails.data.account_name)
      setFieldValue('accountNumber', accountDetails.data.account_number)
    }
  }, [accountDetails])

  return (
    <div className="flex flex-col gap-4 max-w-[800px]">
      <TextInput
        type="text"
        name="accountNumber"
        placeholder="Account Number"
        value={values.accountNumber}
        onBlur={handleBlur}
        onChange={handleChange}
        errors={
          errors.accountNumber && touched.accountNumber
            ? errors.accountNumber
            : ''
        }
        hasIcon={false}
        containerClassName="col-span-full"
      />

      <div className="w-full flex flex-col gap-2">
        <ComboBox
          isDropdown
          type="text"
          name="bankName"
          value={values.bankName}
          onChange={(e) => {
            handleChange(e)
            setFieldValue('bankCode', undefined)
          }}
          onBlur={handleBlur}
          hasIcon={false}
          placeholder="Select Bank"
          errors={
            (touched.bankName || touched.accountNumber) && errors.bankName
              ? errors.bankName
              : ''
          }
          menuItems={
            banks?.data
              ?.filter((b) =>
                b.name
                  .toLowerCase()
                  .includes(query?.search?.toLowerCase() ?? ''),
              )
              ?.map((bank) => ({
                name: `${bank.name}`,
                value: {
                  name: `${bank.name}`,
                  code: IS_LIVE ? bank.code : '001',
                },
              })) ?? []
          }
          onSelectOption={(selected) => {
            setFieldValue('bankCode', selected?.code)
            setFieldValue('bankName', selected?.name)
            setQuery({
              ...query,
              search: undefined,
            })
          }}
          onSearch={(val) => {
            setQuery({
              ...query,
              search: val === '' ? undefined : val,
            })
          }}
          loading={fetchingBanks}
          immediate
          position="bottom start"
          menuClassName="!max-h-[250px] !z-[999] shadow-md"
          className="w-full"
        />

        <p
          className={`text-sm ${
            resolveAccountError ? 'text-danger' : 'text-black'
          }`}
        >
          {resolvingAccount && 'Resolving bank details...'}
          {!resolvingAccount && resolveAccountError
            ? 'Could not resolve bank details...'
            : undefined}
          {!resolvingAccount && !resolveAccountError
            ? values?.accountName
            : undefined}
        </p>
      </div>
    </div>
  )
}
